import { ApolloError, gql, TypedDocumentNode, useQuery, useSuspenseQuery } from '@apollo/client';
import { Interval, toDate } from 'date-fns';
import {
  BatteryForecastDataQuery,
  BatteryForecastDataQueryVariables,
  Forecast,
  ForecastDataQuery,
  ForecastDataQueryVariables,
  Resolution,
} from '../../generated/graphql';

export type { Forecast, ForecastSlice } from '../../generated/graphql';

export const forecastDataQuery: TypedDocumentNode<ForecastDataQuery, ForecastDataQueryVariables> = gql`
  query ForecastData($devices: [ID!]!, $interval: IntervalInput!, $resolution: Resolution!) {
    forecast(devices: $devices, interval: $interval, resolution: $resolution) {
      id
      deviceId
      interval {
        start
        end
      }
      resolution
      slices {
        id
        date
        hasDowntime
        fields {
          type
          watt
          value @client {
            value
            unit
          }
        }
      }
    }
  }
`;

export const batteryForecastDataQuery: TypedDocumentNode<BatteryForecastDataQuery, BatteryForecastDataQueryVariables> =
  gql`
    query batteryForecastData($devices: [ID!]!, $interval: IntervalInput!) {
      batteryForecast(devices: $devices, interval: $interval) {
        id
        deviceId
        interval {
          start
          end
        }
        resolution
        slices {
          id
          date
          hasDowntime
          fields {
            type
            watt
            value @client {
              value
              unit
            }
          }
        }
      }
    }
  `;

export function useForecastData(
  devices: readonly string[],
  interval: Interval,
  resolution?: Resolution,
): [readonly Forecast[] | undefined, ApolloError | undefined] {
  const { data, error } = useSuspenseQuery(forecastDataQuery, {
    variables: {
      devices,
      interval: {
        start: toDate(interval.start),
        end: toDate(interval.end),
      },
      resolution: resolution ?? Resolution.ONE_HOUR,
    },
    errorPolicy: 'all',
  });

  return [data?.forecast, error];
}

export function useBatteryForecastData(
  devices: readonly string[],
  interval: Interval,
): [readonly Forecast[] | undefined, ApolloError | undefined] {
  const { data, error } = useSuspenseQuery(batteryForecastDataQuery, {
    variables: {
      devices,
      interval: {
        start: toDate(interval.start),
        end: toDate(interval.end),
      },
    },
    errorPolicy: 'all',
  });

  return [data?.batteryForecast, error];
}

export function useForecastDataSync(
  devices: readonly string[],
  interval: Interval,
  resolution?: Resolution,
): { loading: boolean; data: readonly Forecast[] | undefined; error: ApolloError | undefined } {
  const { data, loading, error } = useQuery(forecastDataQuery, {
    skip: devices.length === 0,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    variables: {
      devices,
      interval: {
        start: toDate(interval.start),
        end: toDate(interval.end),
      },
      resolution: resolution ?? Resolution.ONE_HOUR,
    },
  });

  return { loading, data: data?.forecast, error };
}
